export interface IdleTimerOptions {
  /** Quiet this long, warn the caller. */
  warnAfterMs: number;
  /** After the warning, quiet this much longer, give up. */
  endAfterMs: number;
  onWarn: () => void;
  onIdle: () => void;
}

/**
 * Warns, then gives up on, a call nobody is talking on.
 *
 * A call bills by the minute whether anyone talks or not, so a forgotten one
 * should end itself, but a caller should never be hung up on unannounced.
 * After `warnAfterMs` of quiet `onWarn` fires (the assistant says the call is
 * about to end); `endAfterMs` later, still quiet, `onIdle` fires.
 *
 * Activity restarts the countdown: `heard` (the caller) always does, `touch`
 * (anything else, like the assistant talking) only before the warning, so the
 * warning itself does not keep the call open. Work the caller is waiting on
 * (`hold`) pauses it entirely: a caller is never hung up on while the
 * assistant is still looking something up.
 */
export class IdleTimer {
  private timer: ReturnType<typeof setTimeout> | null = null;
  private holds = 0;
  private running = false;
  private warned = false;

  constructor(private readonly options: IdleTimerOptions) {}

  /** Begin counting. */
  start(): void {
    this.running = true;
    this.arm();
  }

  /** The caller did something: count from now, warned or not. */
  heard(): void {
    this.warned = false;
    if (this.running) this.arm();
  }

  /** Something else happened: count from now, unless already warned. */
  touch(): void {
    if (this.running && !this.warned) this.arm();
  }

  /**
   * Pause until the returned release is called, e.g. while a request runs.
   * The countdown restarts in full on release. Releasing twice is harmless.
   */
  hold(): () => void {
    this.holds += 1;
    this.clear();
    let released = false;
    return () => {
      if (released) return;
      released = true;
      this.holds -= 1;
      this.heard();
    };
  }

  /** Stop counting for good. */
  stop(): void {
    this.running = false;
    this.clear();
  }

  private arm(): void {
    this.clear();
    if (this.holds > 0) return;
    if (this.warned) {
      this.schedule(this.options.endAfterMs, this.options.onIdle);
      return;
    }
    this.schedule(this.options.warnAfterMs, () => {
      this.warned = true;
      this.options.onWarn();
      this.arm();
    });
  }

  private schedule(delayMs: number, callback: () => void): void {
    this.timer = setTimeout(() => {
      this.timer = null;
      callback();
    }, delayMs);
  }

  private clear(): void {
    if (this.timer) clearTimeout(this.timer);
    this.timer = null;
  }
}
